import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Mic, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { authApi } from '../utils/api';

const ForgotPassword = () => {
  const { t } = useTranslation('auth');
  const { t: tCommon } = useTranslation('common');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState<'email' | 'verify' | 'done'>('email');
  const navigate = useNavigate();

  const handleSendCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const response = await authApi.forgotPassword(email);
      setSuccess(response.data.message);
      setStep('verify');
    } catch (err: any) {
      setError(err.response?.data?.detail || t('forgotPassword.stepEmail.error'));
    } finally {
      setLoading(false);
    }
  };

  const handleReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (newPassword !== confirmPassword) {
      setError(tCommon('errors.passwordMismatch'));
      return;
    }

    if (newPassword.length < 6) {
      setError(tCommon('errors.passwordTooShort'));
      return;
    }

    setLoading(true);
    try {
      const response = await authApi.resetPassword(email, code, newPassword);
      setSuccess(response.data.message);
      setStep('done');
      setTimeout(() => {
        navigate('/login');
      }, 2000);
    } catch (err: any) {
      setError(err.response?.data?.detail || t('forgotPassword.stepVerify.error'));
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setError('');
    setLoading(true);
    try {
      const response = await authApi.forgotPassword(email);
      setSuccess(response.data.message);
    } catch (err: any) {
      setError(err.response?.data?.detail || t('forgotPassword.resendError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-50 to-primary-100">
      <div className="w-full max-w-md">
        <div className="card">
          {/* Logo */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-100 rounded-full mb-4">
              <Mic className="w-8 h-8 text-primary-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900">{t('forgotPassword.title')}</h1>
            <p className="text-gray-500 mt-1">{t('forgotPassword.subtitle')}</p>
          </div>

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm mb-4">
              {error}
            </div>
          )}

          {success && (
            <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-green-600 text-sm mb-4">
              {success}
            </div>
          )}

          {step === 'email' && (
            <>
              <p className="text-gray-600 text-sm mb-6">
                {t('forgotPassword.stepEmail.description')}
              </p>
              <form onSubmit={handleSendCode} className="space-y-5">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('forgotPassword.stepEmail.emailLabel')}
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="input"
                    placeholder={t('forgotPassword.stepEmail.emailPlaceholder')}
                    required
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full btn-primary flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      {t('forgotPassword.stepEmail.loading')}
                    </>
                  ) : (
                    t('forgotPassword.stepEmail.submit')
                  )}
                </button>
              </form>
            </>
          )}

          {step === 'verify' && (
            <form onSubmit={handleReset} className="space-y-5">
              <div className="text-center mb-2">
                <p className="text-gray-600 text-sm">
                  {t('forgotPassword.stepVerify.codeSentTo')} <span className="font-medium text-gray-900">{email}</span>
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('forgotPassword.stepVerify.codeLabel')}
                </label>
                <input
                  type="text"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  className="input text-center text-2xl tracking-[0.5em] font-mono"
                  placeholder={t('forgotPassword.stepVerify.codePlaceholder')}
                  required
                  maxLength={6}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('forgotPassword.stepVerify.newPasswordLabel')}
                </label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="input"
                  placeholder={t('forgotPassword.stepVerify.newPasswordPlaceholder')}
                  required
                  minLength={6}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('forgotPassword.stepVerify.confirmNewPasswordLabel')}
                </label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="input"
                  placeholder={t('forgotPassword.stepVerify.confirmNewPasswordPlaceholder')}
                  required
                  minLength={6}
                />
              </div>

              <button
                type="submit"
                disabled={loading || code.length !== 6}
                className="w-full btn-primary flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    {t('forgotPassword.stepVerify.loading')}
                  </>
                ) : (
                  t('forgotPassword.stepVerify.submit')
                )}
              </button>

              <div className="text-center">
                <button
                  type="button"
                  onClick={handleResend}
                  disabled={loading}
                  className="text-sm text-primary-600 hover:text-primary-700 font-medium"
                >
                  {t('forgotPassword.stepVerify.resend')}
                </button>
              </div>
            </form>
          )}

          {step === 'done' && (
            <div className="text-center py-4">
              <div className="text-4xl mb-4">✅</div>
              <p className="text-gray-700 font-medium">{t('forgotPassword.stepDone.success')}</p>
              <p className="text-gray-500 text-sm mt-2">{t('forgotPassword.stepDone.redirecting')}</p>
            </div>
          )}

          {/* 返回登录 */}
          <div className="mt-6 text-center text-sm text-gray-500">
            <Link to="/login" className="text-primary-600 hover:text-primary-700 font-medium">
              {t('forgotPassword.backToLogin')}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
